import {pinnedColumnShadowStyles} from '@hconnect/uikit'
import {CollapsableRow, customThemeConstants} from '@hconnect/uikit/src/lib2'
import LinkIcon from '@mui/icons-material/Link'
import {Stack, Tooltip, Typography, useTheme} from '@mui/material'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {
  SLA_TABLE_WIDTHS,
  SLA_TABLE_LEFTS,
  SLA_TABLE_COLUMNS,
  SLA_TABLE_COLUMN_KEYS,
  SLA_TABLE_COLUMN_KEYS_TO_MONTH,
  FEATURE_NAMESPACE
} from '../../consts'
import {SlaTableDataRow} from '../../types'

import {SlaKpiTabsTargetColumnChip} from './SlaKpiTabsTargetColumnChip'
import {SlaKpiTabsValuesTableCellMonthItem} from './SlaKpiTabsValuesTableCellMonthItem'

export type SlaKpiTabsValuesTableRowProps = {
  row: SlaTableDataRow
  translationPrefix?: string
  isScrolledToRight: boolean
  isCellEditable: (month: number, year: number) => boolean // whether KPI can be edited if it already contains a value
  isCellCreatable: (month: number, year: number) => boolean // whether KPI can be created if there is missing value
  isDesktop: boolean
  getIsExpanded: (id: string) => boolean
  toggleExpandCollapse: (id: string) => void
  onOpenCommentsVersions: (kpiId: string) => void
  cceKpiNameMap: Map<number, string>
}

export const SlaKpiTabsValuesTableRow = ({
  row,
  isScrolledToRight,
  isDesktop,
  getIsExpanded,
  toggleExpandCollapse,
  isCellEditable,
  isCellCreatable,
  onOpenCommentsVersions,
  cceKpiNameMap
}: SlaKpiTabsValuesTableRowProps) => {
  const theme = useTheme()

  const {t} = useTranslation(FEATURE_NAMESPACE)

  const isExpanded = getIsExpanded(String(row.id))

  const handleExpandToggle = useCallback(() => {
    toggleExpandCollapse(String(row.id))
  }, [toggleExpandCollapse, row.id])

  const isColumnEditable = useCallback(
    (key: string, item: SlaTableDataRow) => {
      const month = SLA_TABLE_COLUMN_KEYS_TO_MONTH[key]
      if (month === undefined) {
        return false
      }
      return isCellEditable(month, item.year)
    },
    [isCellEditable]
  )

  const getCellStyle = useCallback(
    (key: string, item: SlaTableDataRow) => {
      if (key === SLA_TABLE_COLUMNS.DATA_FIELD) {
        const style = {
          width: `${SLA_TABLE_WIDTHS.DATA_FIELD}px`,
          position: 'sticky',
          left: SLA_TABLE_LEFTS.DATA_FIELD,
          zIndex: 200,
          background: theme.palette.background.paper
        }

        if (isScrolledToRight) {
          style['&::after'] = pinnedColumnShadowStyles(theme)
        }

        return style
      }

      if (key === SLA_TABLE_COLUMNS.TARGET) {
        const style = {
          width: `${SLA_TABLE_WIDTHS.TARGET}px`,
          background: theme.palette.background.paper,
          position: isDesktop ? 'sticky' : 'initial',
          left: isDesktop ? SLA_TABLE_LEFTS.TARGET : 'auto',
          zIndex: isDesktop ? 200 : 'auto'
        }

        if (isScrolledToRight) {
          style['&::after'] = pinnedColumnShadowStyles(theme)
        }

        return style
      }

      const isEditable = isColumnEditable(key, item)
      return {
        width: `${SLA_TABLE_WIDTHS.MONTHS}px`,
        background: isEditable
          ? theme.palette.background.paper
          : theme.palette.mode === 'light'
            ? customThemeConstants().palette.neutralFaded10
            : theme.palette.action.hover
      }
    },
    [isColumnEditable, isScrolledToRight, isDesktop, theme]
  )

  const getBoxStyle = () => {
    return {
      whiteSpace: 'normal'
    }
  }

  const renderSlaTableCell = useCallback(
    <T extends SlaTableDataRow>(
      item: T,
      columnIndex: number,
      columnKey: string
    ): React.ReactNode => {
      if (columnKey === SLA_TABLE_COLUMNS.DATA_FIELD) {
        const hasSource =
          item.type === 'item' &&
          item.cceKpiDefinitionId !== null &&
          item.cceKpiDefinitionId !== undefined
        const sourceName =
          item.type === 'item' &&
          item.cceKpiDefinitionId !== null &&
          item.cceKpiDefinitionId !== undefined
            ? cceKpiNameMap.get(item.cceKpiDefinitionId)
            : undefined
        return (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography>{item.dataField}</Typography>
            {hasSource && (
              <Tooltip
                title={t('slaTable.autoFilled.tooltip', {
                  sourceName: sourceName ?? String(item.cceKpiDefinitionId)
                })}
              >
                <LinkIcon fontSize="small" color="action" />
              </Tooltip>
            )}
          </Stack>
        )
      }

      if (item.type !== 'item') {
        return null
      }

      switch (columnKey) {
        case SLA_TABLE_COLUMNS.TARGET:
          return <SlaKpiTabsTargetColumnChip target={item.target} unit={item.unit} />
        default: {
          const month = SLA_TABLE_COLUMN_KEYS_TO_MONTH[columnKey]
          const isEditable = isCellEditable(month, item.year)
          const isCreatable = isCellCreatable(month, item.year)

          return (
            <SlaKpiTabsValuesTableCellMonthItem
              kpiValues={item.kpiValues}
              month={month}
              isEditable={isEditable}
              isCreatable={isCreatable}
              onOpenCommentsVersions={onOpenCommentsVersions}
            />
          )
        }
      }
    },
    [isCellEditable, isCellCreatable, onOpenCommentsVersions, cceKpiNameMap, t]
  )

  const collapseToggleTestIdGenerator = useCallback(
    (itemId: string | number) => `collapse-toggle-${itemId}`,
    []
  )

  const isItemExpanded = useCallback(() => isExpanded, [isExpanded])

  return (
    <CollapsableRow
      item={row}
      columnKeys={SLA_TABLE_COLUMN_KEYS}
      renderCell={renderSlaTableCell}
      isItemExpanded={isItemExpanded}
      onExpandToggleClicked={handleExpandToggle}
      collapseToggleTestIdGenerator={collapseToggleTestIdGenerator}
      getCellStyle={getCellStyle}
      getBoxStyle={getBoxStyle}
    />
  )
}
